import Decimal from 'decimal.js';
import { odoTrueKm, validOdoFactor } from './odo-factor';

/**
 * P12 PR6 – hiệu quả xe & tài xế (docs/phases/P12.md §14, DEC-210…219; anh Tiến duyệt PA Nhân sự v2 09/10/2026).
 * Hàm thuần dùng chung server (quyết định, báo cáo) + giao diện (xem trước): cảnh báo chênh lệch từng lần đổ dầu,
 * lít còn bất thường lúc chốt kỳ, phân bổ lương theo ngày lái, chi phí/km, lốp thay sớm, chênh ERP − Asia.
 * Mọi ngưỡng là tham số lưu DB (`fleet_efficiency_policy`), số dưới đây là mặc định.
 */
const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });

export interface FleetEfficiencyPolicy {
  /** Mốc kỳ dầu (phút sau 00:00 giờ VN ngày 01), mặc định 420 = 07:00 */
  periodAnchorMinute: number;
  /** Lít còn tài xế báo lúc chốt kỳ lệch số tính toán quá % dung tích bình → cờ đỏ */
  closeoutLitresTolerancePct: number;
  /** Lít tiêu hao giữa 2 lần đổ vượt định mức quá % → cảnh báo ngay */
  fillFuelOverPct: number;
  /** Km ODO giữa 2 lần đổ lệch km GPS cùng khoảng quá max(km; %) → cảnh báo ngay. Mặc định 0 km + 5% = đúng 5% km đồng hồ (DEC-290) */
  fillKmMinKm: number;
  fillKmRatioPct: number;
  /** Thay lốp cùng vị trí khi chưa chạy đủ km này so lần thay trước → cảnh báo lốp thay sớm */
  tireMinKm: number;
  /** Chi phí sửa/km của xe > hệ số × bình quân đội xe → cờ đỏ */
  repairRatio: number;
  /** Chênh ERP − Asia quá max(đồng; %) → cờ đỏ */
  asiaDiffMinVnd: number;
  asiaDiffRatioPct: number;
  /** Lần đổ đầu tháng phải chạy tối thiểu % km kế hoạch của lần đổ cuối tháng trước (DEC-482), 0 = tắt */
  firstFillMinRunPct: number;
}

export const FLEET_EFFICIENCY_POLICY_DEFAULT: FleetEfficiencyPolicy = {
  periodAnchorMinute: 7 * 60,
  closeoutLitresTolerancePct: 15,
  // Anh Tiến 11/10/2026 (DEC-290): hao dầu vượt định mức > 5% cảnh báo ngay; lệch km đồng hồ / GPS = 5% km đồng hồ, bỏ sàn 50 km
  fillFuelOverPct: 5,
  fillKmMinKm: 0,
  fillKmRatioPct: 5,
  tireMinKm: 30_000,
  repairRatio: 1.5,
  asiaDiffMinVnd: 500_000,
  asiaDiffRatioPct: 5,
  // Anh Tiến 24/10/2026 (DEC-482): chạy đạt 80% mới cho đổ tiếp ở lần đầu của tháng
  firstFillMinRunPct: 80,
};

export const FLEET_EFFICIENCY_POLICY_LABELS: Record<keyof FleetEfficiencyPolicy, string> = {
  periodAnchorMinute: 'Mốc kỳ dầu ngày 01 (giờ:phút)',
  closeoutLitresTolerancePct: 'Lít còn lúc chốt kỳ lệch tối đa (% dung tích bình)',
  fillFuelOverPct: 'Dầu giữa 2 lần đổ vượt định mức tối đa (%)',
  fillKmMinKm: 'Km ODO lệch GPS giữa 2 lần đổ: tối thiểu (km, 0 = chỉ theo %)',
  fillKmRatioPct: 'Km ODO lệch GPS giữa 2 lần đổ: tỷ lệ (%)',
  tireMinKm: 'Lốp thay sớm khi chưa chạy đủ (km)',
  repairRatio: 'Chi phí sửa/km gấp bình quân đội xe (lần)',
  asiaDiffMinVnd: 'Chênh ERP − Asia tối thiểu (đ)',
  asiaDiffRatioPct: 'Chênh ERP − Asia tỷ lệ (%)',
  firstFillMinRunPct: 'Lần đổ đầu tháng: chạy tối thiểu (% kế hoạch lần đổ cuối tháng trước, 0 = tắt)',
};

const r1 = (v: number) => Math.round(v * 10) / 10;
const r2 = (v: number) => Math.round(v * 100) / 100;
const fmt = (v: number, d = 1) => v.toLocaleString('vi-VN', { maximumFractionDigits: d });

// ---------------------------------------------------------------------------
// 9.1 Lít còn lúc chốt kỳ: còn đầu kỳ + đổ − định mức × km GPS / 100; lệch > 15% bình → cờ đỏ
// ---------------------------------------------------------------------------
export interface CloseoutLitresInput {
  openingLitres: number | null;
  filledLitres: number;
  norm: number | null;
  gpsKm: number | null;
  tankLitres: number | null;
  reportedLitres: number;
  tolerancePct: number;
  /** DEC-291: lít dầu cẩu trong kỳ (phút cẩu × lít/giờ cẩu), tính vào lít đã dùng */
  craneLitres?: number | null;
}
export interface CloseoutLitresCheck {
  status: 'ok' | 'flag' | 'skip';
  expected: number | null;
  diff: number | null;
  limit: number | null;
  message: string;
}

export function closeoutLitresCheck(i: CloseoutLitresInput): CloseoutLitresCheck {
  const missing = [
    i.openingLitres === null ? 'số dư đầu kỳ' : null,
    !i.norm || i.norm <= 0 ? 'định mức' : null,
    i.gpsKm === null ? 'km GPS' : null,
    !i.tankLitres || i.tankLitres <= 0 ? 'dung tích bình' : null,
  ].filter(Boolean);
  if (missing.length) return { status: 'skip', expected: null, diff: null, limit: null, message: `Chưa đối chiếu được lít còn: thiếu ${missing.join(', ')}` };
  const crane = i.craneLitres && i.craneLitres > 0 ? i.craneLitres : 0;
  const expected = r2(i.openingLitres! + i.filledLitres - (i.norm! * i.gpsKm!) / 100 - crane);
  const diff = r2(i.reportedLitres - expected);
  const limit = r2((i.tankLitres! * i.tolerancePct) / 100);
  const head = `Báo còn ${fmt(i.reportedLitres)} L, tính theo định mức còn ${fmt(expected)} L (đầu kỳ ${fmt(i.openingLitres!)} + đổ ${fmt(i.filledLitres)} − ${fmt(i.norm!)} L/100km × ${fmt(i.gpsKm!)} km GPS${crane ? ` − ${fmt(crane, 2)} L cẩu` : ''})`;
  if (Math.abs(diff) > limit)
    return { status: 'flag', expected, diff, limit, message: `${head}: lệch ${fmt(diff)} L, quá ${fmt(i.tolerancePct)}% bình (${fmt(limit)} L)` };
  return { status: 'ok', expected, diff, limit, message: `${head}: lệch ${fmt(diff)} L, trong ${fmt(i.tolerancePct)}% bình` };
}

// ---------------------------------------------------------------------------
// Cảnh báo ngay tại từng lần đổ dầu (bổ sung anh Tiến 09/10: "sai số không để dồn cuối tháng").
// Anh Tiến 11/10/2026 (DEC-290): mỗi lần đổ so 2 cách (km đồng hồ và km GPS → L/100 km, km/L); lệch km > 5% km đồng hồ;
// hao dầu vượt định mức > 5% → 2 câu khác nhau: quản lý thấy định mức, tài xế KHÔNG thấy định mức.
// ---------------------------------------------------------------------------
export interface FuelConsumption {
  km: number;
  /** lít / 100 km */
  lPer100: number;
  /** km / lít */
  kmPerL: number;
}

/** Tiêu hao của chặng: L/100 km và km/L (làm tròn 2 số lẻ ROUND_HALF_UP). Thiếu lít hoặc km ≤ 0 → null. */
export function fuelConsumption(litres: number | null | undefined, km: number | null | undefined): FuelConsumption | null {
  if (litres == null || km == null || !(km > 0) || !(litres > 0)) return null;
  return {
    km: r1(km),
    lPer100: new D(litres).times(100).div(km).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber(),
    kmPerL: new D(km).div(litres).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber(),
  };
}

/**
 * Hiệu suất tính cho lần đổ trước (anh Tiến 19/10/2026, giống Minhtien-CRM): số lít lần đổ trước (hoặc lít còn lúc chốt kỳ)
 * chia cho km xe chạy từ lần đó tới lần đổ này, theo km đồng hồ và theo km định vị.
 * Mức tối thiểu = 100 / định mức (km/L). Xét "chạy dưới định mức" theo km nhỏ hơn của ODO và GPS (như km khoán DEC-181):
 * km/L < mức tối thiểu × (1 − dung sai 5%) → tài xế phải kiểm tra + giải trình ngay.
 * DEC-503 (anh Tiến 26/10/2026 "Đồng ý"): dung sai 5% = mức lệch km ODO–GPS cho phép, tránh bắt giải trình oan vì sai số GPS.
 */
export const UNDER_NORM_TOLERANCE_PCT = 5;

export interface PrevFillEfficiency {
  prevLitres: number;
  odo: FuelConsumption | null;
  gps: FuelConsumption | null;
  /** Mức tối thiểu km/L và L/100 km; null = xe chưa có định mức (bản cho tài xế cũng để null) */
  normKmPerL: number | null;
  normLPer100: number | null;
  /** (km/L thực tế ÷ mức tối thiểu − 1) × 100, âm = dưới mức */
  odoPct: number | null;
  gpsPct: number | null;
  /** km/L dùng để xét = theo min(km ODO thật, km GPS) */
  basisKmPerL: number | null;
  /** DEC-562: km ODO đã trừ sai số đồng hồ của xe (chỉ giám đốc thấy); null = xe không ghi nhận sai số */
  odoKmTrue?: number | null;
  /** Ngưỡng bắt giải trình = mức tối thiểu × (1 − UNDER_NORM_TOLERANCE_PCT%); null = không có định mức */
  explainKmPerL: number | null;
  below: boolean;
}

export function prevFillEfficiency(i: {
  prevLitres: number | null | undefined;
  odoKm: number | null | undefined;
  gpsKm: number | null | undefined;
  norm: number | null | undefined;
  /** DEC-562: sai số đồng hồ (km ODO ÷ km GPS lúc bình thường); xét dưới mức theo km ODO đã trừ sai số */
  odoFactor?: number | null;
}): PrevFillEfficiency | null {
  if (i.prevLitres == null || !(i.prevLitres > 0)) return null;
  const odo = fuelConsumption(i.prevLitres, i.odoKm);
  const gps = fuelConsumption(i.prevLitres, i.gpsKm);
  if (!odo && !gps) return null;
  const norm = i.norm != null && i.norm > 0 ? i.norm : null;
  const normKmPerL = norm ? new D(100).div(norm).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber() : null;
  const pct = (c: FuelConsumption | null) =>
    c && norm ? new D(c.km).div(i.prevLitres!).times(norm).div(100).minus(1).times(100).toDecimalPlaces(1, Decimal.ROUND_HALF_UP).toNumber() : null;
  const factor = validOdoFactor(i.odoFactor) && i.odoFactor !== 1 ? i.odoFactor : null;
  const odoTrue = odo && factor ? odoTrueKm(odo.km, factor) : (odo?.km ?? null);
  const basisKm = odoTrue !== null && gps ? Math.min(odoTrue, gps.km) : (odoTrue ?? gps!.km);
  const basisKmPerL = new D(basisKm).div(i.prevLitres).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber();
  return {
    prevLitres: i.prevLitres,
    odo,
    gps,
    normKmPerL,
    normLPer100: norm,
    odoPct: pct(odo),
    gpsPct: pct(gps),
    basisKmPerL,
    ...(factor ? { odoKmTrue: odoTrue } : {}),
    explainKmPerL: norm ? new D(100).div(norm).times(100 - UNDER_NORM_TOLERANCE_PCT).div(100).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toNumber() : null,
    // km × định mức < lít × 100 × (1 − dung sai)
    below: norm !== null && new D(basisKm).times(norm).lt(new D(i.prevLitres).times(100 - UNDER_NORM_TOLERANCE_PCT)),
  };
}

export interface FuelOverCost {
  /** Lít hao thêm so định mức = (thực tế − định mức) × km / 100, làm tròn 2 số lẻ */
  extraLitres: number;
  /** Tiền hao thêm = lít hao thêm × đơn giá lần đổ, VND không số lẻ (ROUND_HALF_UP); không có đơn giá → null */
  extraMoney: number | null;
}

/**
 * Hao thêm so định mức của 1 chặng. Ví dụ anh Tiến: định mức 25 L/100 km, chặng 412 km, thực tế 31,25 L/100 km
 * → vượt 6,25 L/100 km → 25,75 L × 20.500 đ = 527.875 đ.
 */
export function fuelOverCost(i: { norm: number; km: number; actualPer100: number; unitPrice?: number | null }): FuelOverCost {
  const extra = new D(i.actualPer100).minus(i.norm).times(i.km).div(100).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
  return {
    extraLitres: extra.toNumber(),
    extraMoney: i.unitPrice ? extra.times(i.unitPrice).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber() : null,
  };
}

const fmtVnd = (v: number) => Math.round(v).toLocaleString('vi-VN');
const plateText = (p: string | null | undefined) => (p ? `Xe ${p}` : 'Xe');

/** Câu cảnh báo lệch km (giống nhau cho tài xế và quản lý, không có định mức). */
export function kmGapMessage(i: { odoKm: number; gpsKm: number; pct: number | null; limitKm: number | null; odoKmTrue?: number | null; hideDiff?: boolean }): string {
  const head = `Định vị và thực tế chênh lệch quá lớn: km đồng hồ ${fmt(i.odoKm)}`;
  // DEC-562 (giám đốc): km đồng hồ đã trừ sai số của xe
  const adj = i.odoKmTrue != null ? ` (trừ sai số đồng hồ còn ${fmt(i.odoKmTrue)})` : '';
  if (i.hideDiff) return `${head}, km GPS ${fmt(i.gpsKm)}`;
  const base = i.odoKmTrue ?? i.odoKm;
  return `${head}${adj}, km GPS ${fmt(i.gpsKm)}, lệch ${i.pct !== null ? `${fmt(i.pct)}%` : `${fmt(Math.abs(base - i.gpsKm))} km`}${i.limitKm !== null ? ` (cho phép ${fmt(i.limitKm)} km)` : ''}`;
}

/** Câu cho quản lý (fleet.manage, fleet.fuel_approve): có định mức. */
export function fuelOverManagerMessage(i: {
  plateNo?: string | null;
  actualPer100: number;
  norm: number;
  overPct: number;
  km: number;
  extraLitres: number;
  extraMoney: number | null;
  odoKm: number | null;
  gpsKm: number | null;
}): string {
  const money = i.extraMoney !== null ? ` ≈ ${fmtVnd(i.extraMoney)} đ` : '';
  return (
    `${plateText(i.plateNo)} hao dầu: ${fmt(i.actualPer100, 2)} L/100 km, định mức ${fmt(i.norm, 2)} (vượt ${fmt(i.overPct)}%). ` +
    `Chặng ${fmt(i.km)} km, hao thêm ${fmt(i.extraLitres, 2)} L${money}. ` +
    `Km đồng hồ ${i.odoKm !== null ? fmt(i.odoKm) : 'chưa có'}, km GPS ${i.gpsKm !== null ? fmt(i.gpsKm) : 'chưa có'}. Đề nghị kiểm tra xe.`
  );
}

/** Câu cho tài xế: KHÔNG có số định mức, không có L/100 km định mức (anh Tiến 11/10/2026: định mức chỉ quản lý biết). */
export function fuelOverDriverMessage(i: { plateNo?: string | null; km: number; extraLitres: number; extraMoney: number | null }): string {
  const money = i.extraMoney !== null ? ` ≈ ${fmtVnd(i.extraMoney)} đ` : '';
  return `${plateText(i.plateNo)} đang hao dầu hơn bình thường: chặng ${fmt(i.km)} km hao thêm khoảng ${fmt(i.extraLitres, 2)} L${money}. Kiểm tra và ghi nguyên nhân.`;
}

export interface FillVarianceInput {
  /** Mốc trước: phiếu đổ trước (đổ đầy) hoặc lần chốt kỳ (lít còn); null = phiếu đầu tiên */
  previous: { odometerKm: number; source: 'fuel' | 'closeout'; litresLeft?: number | null } | null;
  odometerKm: number;
  litres: number;
  fullTank: boolean;
  /** Km GPS cùng khoảng (null = chưa có GPS) */
  gpsKm: number | null;
  norm: number | null;
  tankLitres: number | null;
  policy: Pick<FleetEfficiencyPolicy, 'fillFuelOverPct' | 'fillKmMinKm' | 'fillKmRatioPct'>;
  /** Đơn giá lần đổ này (đ/lít) để quy tiền hao thêm */
  unitPrice?: number | null;
  /** Biển số để ghép câu cảnh báo */
  plateNo?: string | null;
  /** P12 §16 (DEC-291): lít dầu cẩu trong chặng (phút cẩu × lít/giờ cẩu), trừ khỏi lít dùng trước khi so tiêu hao theo km */
  craneLitres?: number | null;
  /** DEC-562: sai số đồng hồ của xe (km ODO ÷ km GPS lúc bình thường); so lệch km và km định mức theo km ODO đã trừ sai số */
  odoFactor?: number | null;
}
export interface FillVariance {
  odoKm: number | null;
  gpsKm: number | null;
  kmDiff: number | null;
  kmLimit: number | null;
  kmFlag: boolean;
  /** Km dùng để tính định mức: min(ODO, GPS) như km khoán DEC-181 */
  kmBasis: number | null;
  usedLitres: number | null;
  allowedLitres: number | null;
  overPct: number | null;
  fuelFlag: boolean;
  flagged: boolean;
  /** Câu cho quản lý (có định mức) */
  messages: string[];
  // --- DEC-290 (phiếu trước 11/10/2026 không có các trường dưới) ---
  /** Lệch km theo % km đồng hồ */
  kmDiffPct?: number | null;
  /** Tiêu hao chặng theo km đồng hồ */
  odo?: FuelConsumption | null;
  /** Tiêu hao chặng theo km GPS */
  gps?: FuelConsumption | null;
  /** L/100 km thực tế trên km dùng tính định mức (min ODO, GPS) */
  actualPer100?: number | null;
  extraLitres?: number | null;
  extraMoney?: number | null;
  /** Câu cho tài xế: không có định mức */
  driverMessages?: string[];
  // --- DEC-291 giờ cẩu ---
  /** Lít dầu cẩu đã trừ (null = không có giờ cẩu / xe chưa có định mức cẩu) */
  craneLitres?: number | null;
  /** Lít chạy xe = lít dùng − lít cẩu; tiêu hao 2 cách và so định mức tính trên số này */
  netLitres?: number | null;
  // --- DEC-562 sai số đồng hồ (chỉ giám đốc thấy) ---
  odoFactor?: number | null;
  /** Km ODO đã trừ sai số; lệch km, lệch % và km định mức tính trên số này */
  odoKmTrue?: number | null;
}

export function fillVariance(i: FillVarianceInput): FillVariance {
  const out: FillVariance = {
    odoKm: null,
    gpsKm: i.gpsKm,
    kmDiff: null,
    kmLimit: null,
    kmFlag: false,
    kmBasis: null,
    usedLitres: null,
    allowedLitres: null,
    overPct: null,
    fuelFlag: false,
    flagged: false,
    messages: [],
    kmDiffPct: null,
    odo: null,
    gps: null,
    actualPer100: null,
    extraLitres: null,
    extraMoney: null,
    driverMessages: [],
    craneLitres: null,
    netLitres: null,
  };
  if (!i.previous) return { ...out, messages: ['Phiếu đầu tiên của xe, chưa có mốc để so'], driverMessages: [] };
  const odoKm = r1(i.odometerKm - i.previous.odometerKm);
  out.odoKm = odoKm;
  // DEC-562: đồng hồ xe đếm lệch cố định → so GPS bằng km ODO đã trừ sai số; tài xế không thấy hệ số
  const factor = validOdoFactor(i.odoFactor) && i.odoFactor !== 1 ? i.odoFactor : null;
  const odoTrue = factor ? odoTrueKm(odoKm, factor) : odoKm;
  if (factor) {
    out.odoFactor = factor;
    out.odoKmTrue = odoTrue;
  }
  if (i.gpsKm !== null) {
    out.kmDiff = r1(odoTrue - i.gpsKm);
    out.kmLimit = r1(Math.max(i.policy.fillKmMinKm, (Math.abs(odoTrue) * i.policy.fillKmRatioPct) / 100));
    out.kmDiffPct = odoTrue > 0 ? r1((Math.abs(out.kmDiff) / odoTrue) * 100) : null;
    out.kmFlag = Math.abs(out.kmDiff) > out.kmLimit;
    if (out.kmFlag) {
      out.messages.push(kmGapMessage({ odoKm, gpsKm: i.gpsKm, pct: out.kmDiffPct, limitKm: out.kmLimit, odoKmTrue: factor ? odoTrue : null }));
      // Tài xế: không có % và km cho phép khi xe có sai số (suy ra được hệ số)
      out.driverMessages!.push(kmGapMessage({ odoKm, gpsKm: i.gpsKm, pct: factor ? null : out.kmDiffPct, limitKm: factor ? null : out.kmLimit, hideDiff: !!factor }));
    }
  }
  out.kmBasis = i.gpsKm !== null ? r1(Math.min(odoTrue, i.gpsKm)) : odoTrue;
  // Lít đã dùng giữa 2 mốc: chỉ tính được khi lần này đổ đầy bình
  if (i.fullTank) {
    if (i.previous.source === 'fuel') out.usedLitres = r2(i.litres);
    else if (i.tankLitres && i.previous.litresLeft != null) out.usedLitres = r2(Math.max(0, i.previous.litresLeft + i.litres - i.tankLitres));
  }
  // DEC-291: dầu cẩu (phút cẩu × lít/giờ) không phải dầu chạy xe → trừ ra trước khi tính tiêu hao theo km, xe cẩu không bị báo hao oan
  const net = out.usedLitres === null ? null : i.craneLitres && i.craneLitres > 0 ? r2(Math.max(0, new D(out.usedLitres).minus(i.craneLitres).toNumber())) : out.usedLitres;
  if (i.craneLitres && i.craneLitres > 0) out.craneLitres = r2(i.craneLitres);
  out.netLitres = net;
  out.odo = fuelConsumption(net, odoKm);
  out.gps = fuelConsumption(net, i.gpsKm);
  if (net !== null && out.kmBasis > 0) out.actualPer100 = fuelConsumption(net, out.kmBasis)?.lPer100 ?? null;
  if (net !== null && i.norm && i.norm > 0 && out.kmBasis > 0) {
    out.allowedLitres = r2((i.norm * out.kmBasis) / 100);
    out.overPct = r1(((net - out.allowedLitres) / out.allowedLitres) * 100);
    out.fuelFlag = out.overPct > i.policy.fillFuelOverPct;
    // Hao thêm = lít chạy xe − định mức × km / 100 (= (thực tế − định mức) × km / 100), tính bằng Decimal
    const extra = new D(net).minus(new D(i.norm).times(out.kmBasis).div(100)).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
    out.extraLitres = extra.toNumber();
    out.extraMoney = i.unitPrice ? extra.times(i.unitPrice).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber() : null;
    if (out.fuelFlag) {
      out.messages.push(
        fuelOverManagerMessage({
          plateNo: i.plateNo,
          actualPer100: out.actualPer100 ?? 0,
          norm: i.norm,
          overPct: out.overPct,
          km: out.kmBasis,
          extraLitres: out.extraLitres,
          extraMoney: out.extraMoney,
          odoKm,
          gpsKm: i.gpsKm,
        }),
      );
      out.driverMessages!.push(fuelOverDriverMessage({ plateNo: i.plateNo, km: out.kmBasis, extraLitres: out.extraLitres, extraMoney: out.extraMoney }));
    }
  }
  out.flagged = out.kmFlag || out.fuelFlag;
  return out;
}

/**
 * Bản cho màn tài xế: bỏ mọi số suy thẳng ra định mức (lít định mức, % vượt) và thay câu quản lý bằng câu tài xế.
 * Anh Tiến 11/10/2026: định mức chỉ quản lý biết.
 */
export function driverSafeVariance(v: FillVariance | null): FillVariance | null {
  if (!v) return null;
  // DEC-291: định mức lít/giờ cẩu cũng chỉ quản lý biết → bỏ lít cẩu, lít chạy xe; tiêu hao 2 cách tính lại trên lít dùng (không trừ cẩu)
  const crane = v.craneLitres != null && v.craneLitres > 0;
  return {
    ...v,
    allowedLitres: null,
    overPct: null,
    craneLitres: null,
    netLitres: null,
    // DEC-562: sai số đồng hồ chỉ giám đốc biết → tài xế thấy lệch km theo số đồng hồ thô
    ...(v.odoFactor != null ? { odoFactor: undefined, odoKmTrue: undefined, kmLimit: null, kmDiffPct: null, kmDiff: v.odoKm !== null && v.gpsKm !== null ? r1(v.odoKm - v.gpsKm) : v.kmDiff } : {}),
    ...(crane ? { odo: fuelConsumption(v.usedLitres, v.odoKm), gps: fuelConsumption(v.usedLitres, v.gpsKm), actualPer100: null } : {}),
    messages: v.driverMessages ?? (v.flagged ? ['Lần đổ này có chênh lệch, quản lý sẽ kiểm tra'] : []),
  };
}

// ---------------------------------------------------------------------------
// Km GPS giữa 2 thời điểm (DEC-290): mẫu 30 phút (km trong ngày, GPS tự về 0 lúc nửa đêm giờ VN) + bảng km ngày
// ---------------------------------------------------------------------------
export interface GpsDayKm {
  /** Ngày giờ VN YYYY-MM-DD */
  day: string;
  km: number;
  /** Quản lý nhập tay km ngày → bỏ mẫu của ngày đó, dùng số nhập tay */
  manual: boolean;
}
export interface GpsKmSample {
  at: Date | string;
  /** Km trong ngày tại thời điểm lấy mẫu */
  km: number;
}
export interface GpsKmBetween {
  km: number | null;
  /** true = cả 2 đầu đều lấy theo mẫu (đúng giờ); false = có đầu phải lấy theo cả ngày */
  exact: boolean;
  /** Km trong ngày tại mốc cuối (mẫu gần nhất trước mốc), null = ngày đó không có mẫu */
  toDayKm: number | null;
  /** Ngày (giờ VN) trong chặng không có số GPS nào → km = null (không cộng thiếu thành số nhỏ sai) */
  missingDays: string[];
}

const VN_MS = 7 * 3_600_000;
export const vnDayOf = (d: Date | string) => new Date(new Date(d).getTime() + VN_MS).toISOString().slice(0, 10);
const nextDay = (d: string) => new Date(Date.parse(`${d}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);

/** Trước giờ này (giờ VN) xe chưa chạy: mốc đầu rơi vào ngày không có mẫu thì vẫn tính trọn ngày đó */
export const GPS_DAY_START_HOUR_VN = 6;

/**
 * Mẫu km trong ngày còn mang số của hôm trước (22/10/2026, xe 61H15745: lấy lúc 00:01 và 00:03 ngày 22/10, GPS gửi lúc 23:55 ngày 21/10,
 * km 151,5; tới 00:05 mới về 0). Trong 1 ngày giờ VN, km chỉ tăng; lần tụt (thấp hơn mẫu trước > 0,5 km) xảy ra trước 06:00
 * nghĩa là các mẫu trước đó là số hôm trước → bỏ. Lần tụt sau 06:00 không đụng tới (không đoán).
 * `list` là mẫu của đúng 1 ngày; trả về theo thứ tự thời gian.
 */
export function dropMidnightCarryover<T extends { at: number | Date | string; km: number }>(list: T[]): T[] {
  const sorted = [...list].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  let cut = 0;
  sorted.forEach((x, k) => {
    const before = sorted[k - 1];
    if (before && Number(x.km) < Number(before.km) - 0.5 && new Date(new Date(x.at).getTime() + VN_MS).getUTCHours() < GPS_DAY_START_HOUR_VN) cut = k;
  });
  return cut ? sorted.slice(cut) : sorted;
}

/**
 * Km GPS xe chạy từ `fromAt` tới `toAt`.
 * - Mốc trong ngày = mẫu gần nhất trước mốc (chưa có mẫu nào trước mốc trong ngày đó = 0 km, đầu ngày).
 * - Cùng ngày: mốc cuối − mốc đầu. Khác ngày: (cả ngày đầu − mốc đầu) + các ngày giữa + mốc cuối.
 * - Ngày không có mẫu (hoặc km ngày nhập tay): theo bảng km ngày – ngày đầu bỏ (mốc đầu trước 06:00 thì lấy trọn ngày),
 *   ngày cuối lấy trọn (mốc cuối trước 06:00 thì 0 km).
 * - Ngày giữa / ngày cuối (hoặc ngày đầu khi cần lấy trọn) không có số GPS nào → km = null, kèm danh sách ngày thiếu
 *   (21/10/2026: trước đây cộng thiếu thành 13,2 km cho chặng 9 ngày).
 */
export function gpsKmBetween(i: { fromAt: Date | string; toAt: Date | string; days: GpsDayKm[]; samples: GpsKmSample[] }): GpsKmBetween {
  const from = new Date(i.fromAt).getTime();
  const to = new Date(i.toAt).getTime();
  const d1 = vnDayOf(i.fromAt);
  const d2 = vnDayOf(i.toAt);
  const daily = new Map(i.days.map((d) => [d.day, d]));
  const byDay = new Map<string, { at: number; km: number }[]>();
  for (const s of i.samples) {
    const k = vnDayOf(s.at);
    if (daily.get(k)?.manual) continue;
    const list = byDay.get(k) ?? [];
    list.push({ at: new Date(s.at).getTime(), km: Number(s.km) });
    byDay.set(k, list);
  }
  // Ngày có mẫu km của hôm trước dính sang sau nửa đêm: bỏ các mẫu đó, bảng km ngày (đã lấy số lớn nhất, gồm cả số dính) không dùng
  const carried = new Set<string>();
  for (const [d, list] of byDay) {
    const clean = dropMidnightCarryover(list);
    if (clean.length !== list.length) {
      carried.add(d);
      byDay.set(d, clean);
    }
  }
  const dayTotal = (d: string): number | null => {
    const row = daily.get(d);
    if (row?.manual) return row.km;
    const s = byDay.get(d);
    const maxS = s?.length ? Math.max(...s.map((x) => x.km)) : null;
    if (carried.has(d)) return maxS;
    if (row == null && maxS === null) return null;
    return Math.max(row?.km ?? 0, maxS ?? 0);
  };
  const markAt = (d: string, t: number): number | null => {
    const s = byDay.get(d);
    if (!s?.length) return null;
    let best: { at: number; km: number } | null = null;
    for (const x of s) if (x.at <= t && (!best || x.at > best.at)) best = x;
    return Math.min(best ? best.km : 0, dayTotal(d) ?? Infinity);
  };
  if (!(to > from)) return { km: null, exact: false, toDayKm: markAt(d2, to), missingDays: [] };
  const m1 = markAt(d1, from);
  const m2 = markAt(d2, to);
  if (d1 === d2) {
    if (m1 === null || m2 === null) return { km: null, exact: false, toDayKm: m2, missingDays: dayTotal(d1) === null ? [d1] : [] };
    return { km: r1(Math.max(0, m2 - m1)), exact: true, toDayKm: m2, missingDays: [] };
  }
  let km = 0;
  const missing: string[] = [];
  const t1 = dayTotal(d1);
  if (m1 !== null && t1 !== null) km += Math.max(0, t1 - m1);
  else if (new Date(from + VN_MS).getUTCHours() < GPS_DAY_START_HOUR_VN) {
    if (t1 === null) missing.push(d1);
    else km += t1;
  }
  for (let d = nextDay(d1); d < d2; d = nextDay(d)) {
    const t = dayTotal(d);
    if (t === null) missing.push(d);
    else km += t;
  }
  // Ngày cuối không có mẫu: mốc trước 06:00 thì xe chưa chạy ngày đó (0 km), sau đó lấy trọn ngày như cũ
  const last = m2 ?? (new Date(to + VN_MS).getUTCHours() < GPS_DAY_START_HOUR_VN ? 0 : dayTotal(d2));
  if (last === null) missing.push(d2);
  else km += last;
  return { km: missing.length ? null : r1(km), exact: m1 !== null && m2 !== null, toDayKm: m2, missingDays: missing };
}

/** Số ODO đọc được tại 1 thời điểm (nhận/trả xe, phiếu đổ dầu, chốt kỳ có mốc giờ) */
export interface OdoReading {
  at: Date | string;
  odometerKm: number;
}

/**
 * Bỏ số ODO nhập nhảy (01/11/2026: xe 61H06971 nhận/trả xe đầu tháng nhập 140.558 trong khi các số sau đó chỉ 139.4xx):
 * số nào lớn hơn một số đọc SAU nó thì bỏ. Trả về theo thứ tự thời gian.
 */
export function cleanOdoReadings(list: OdoReading[]): { at: number; odo: number }[] {
  const sorted = list
    .map((r) => ({ at: new Date(r.at).getTime(), odo: Number(r.odometerKm) }))
    .filter((r) => Number.isFinite(r.at) && Number.isFinite(r.odo) && r.odo > 0)
    .sort((a, b) => a.at - b.at);
  const out: { at: number; odo: number }[] = [];
  let minAfter = Infinity;
  for (let k = sorted.length - 1; k >= 0; k--) {
    const r = sorted[k]!;
    if (r.odo <= minAfter) {
      out.push(r);
      minAfter = r.odo;
    }
  }
  return out.reverse();
}

/** Số ODO tại thời điểm `t` nội suy tuyến tính giữa 2 lần đọc kề nhau; ngoài khoảng có số đọc → null (không đoán) */
export function odoAt(readings: { at: number; odo: number }[], t: number): number | null {
  if (!readings.length || t < readings[0]!.at || t > readings[readings.length - 1]!.at) return null;
  for (let k = 0; k < readings.length; k++) {
    const b = readings[k]!;
    if (b.at === t) return b.odo;
    if (b.at > t) {
      const a = readings[k - 1]!;
      return a.odo + ((b.odo - a.odo) * (t - a.at)) / (b.at - a.at);
    }
  }
  return null;
}

export interface GpsKmPeriod {
  /** Km GPS cả kỳ = km đo được + km ước tính ngày thiếu (ngày không bù được tính 0, nằm trong `missingDays` để cảnh báo) */
  km: number | null;
  /** Km GPS đo được (không gồm ngày thiếu) */
  measuredKm: number;
  /** Km bù cho ngày thiếu GPS = km ODO ngày đó ÷ hệ số sai số đồng hồ */
  estimatedKm: number;
  /** Ngày (giờ VN) thiếu GPS đã bù bằng ODO */
  estimatedDays: string[];
  /** Ngày thiếu GPS mà không có số ODO trước và sau để bù */
  missingDays: string[];
}

/**
 * Km GPS cả kỳ dầu, cắt đúng mốc kỳ (07:00 ngày 01 → 07:00 ngày 01 tháng sau) – anh Tiến 01/11/2026, kỳ 10/2026 GPS thiếu
 * ngày 04, 08–14/10 nên Km GPS thấp hơn ODO 900–1.700 km và khoán dầu trừ oan tài xế. Anh chọn "ERP tự bù từ ODO":
 * - Km đo được theo `gpsKmBetween`; ngày đầu kỳ không có mẫu 30 phút thì lấy trọn ngày (km ngày CRM / bảng km ngày),
 *   ngày cuối (ngày 01 tháng sau) không có mẫu thì dừng ở 00:00 ngày đó.
 * - Ngày không có số GPS nào: km = (ODO nội suy cuối ngày − đầu ngày, trong phạm vi kỳ) ÷ hệ số sai số đồng hồ của xe.
 * - Không có số ODO trước và sau ngày đó → tính 0 km như trước, để trong `missingDays` cho bảng cảnh báo đỏ.
 */
export function gpsKmForPeriod(i: {
  fromAt: Date | string;
  toAt: Date | string;
  days: GpsDayKm[];
  samples: GpsKmSample[];
  readings: OdoReading[];
  odoFactor: number | null | undefined;
}): GpsKmPeriod {
  const d1 = vnDayOf(i.fromAt);
  const hasSampleD1 = i.samples.some((s) => vnDayOf(s.at) === d1);
  const fromAt = hasSampleD1 ? i.fromAt : new Date(Date.parse(`${d1}T00:00:00Z`) - VN_MS);
  // Ngày cuối kỳ (ngày 01 tháng sau) chưa có mẫu 30 phút: tính tới 00:00 ngày đó (không lấy trọn ngày, không coi là thiếu)
  const d2 = vnDayOf(i.toAt);
  const hasSampleD2 = i.samples.some((s) => vnDayOf(s.at) === d2);
  const toAt = hasSampleD2 || d2 === d1 ? i.toAt : new Date(Date.parse(`${d2}T00:00:00Z`) - VN_MS);
  const base = gpsKmBetween({ fromAt, toAt, days: i.days, samples: i.samples });
  if (!base.missingDays.length) return { km: base.km, measuredKm: base.km ?? 0, estimatedKm: 0, estimatedDays: [], missingDays: [] };
  // Km đo được: tính lại với ngày thiếu = 0 km
  const zero = base.missingDays.map((day) => ({ day, km: 0, manual: true }));
  const measuredKm = gpsKmBetween({ fromAt, toAt, days: [...i.days.filter((d) => !base.missingDays.includes(d.day)), ...zero], samples: i.samples }).km ?? 0;
  const readings = cleanOdoReadings(i.readings);
  const from = new Date(i.fromAt).getTime();
  const to = new Date(i.toAt).getTime();
  let estimatedKm = 0;
  const estimatedDays: string[] = [];
  const missingDays: string[] = [];
  for (const day of base.missingDays) {
    const s = Math.max(from, Date.parse(`${day}T00:00:00Z`) - VN_MS);
    const e = Math.min(to, Date.parse(`${day}T00:00:00Z`) - VN_MS + 86_400_000);
    const a = odoAt(readings, s);
    const b = odoAt(readings, e);
    if (a === null || b === null) {
      missingDays.push(day);
      continue;
    }
    estimatedKm += odoTrueKm(Math.max(0, b - a), validOdoFactor(i.odoFactor) ? i.odoFactor : null);
    estimatedDays.push(day);
  }
  estimatedKm = r1(estimatedKm);
  return { km: r1(measuredKm + estimatedKm), measuredKm: r1(measuredKm), estimatedKm, estimatedDays, missingDays };
}

// ---------------------------------------------------------------------------
// Tiền: phân bổ theo ngày lái, chi phí/đơn vị, chênh Asia
// ---------------------------------------------------------------------------
/**
 * Chia `amount` (VND, số nguyên) theo số ngày từng phần. Mỗi phần làm tròn ROUND_HALF_UP; phần dư do làm tròn
 * cộng vào phần nhiều ngày nhất để tổng đúng bằng `amount`.
 */
export function allocateByDays<K extends string | number>(amount: number, days: Map<K, number>): Map<K, number> {
  const out = new Map<K, number>();
  const total = [...days.values()].reduce((a, b) => a + Math.max(0, b), 0);
  if (!total) return out;
  let sum = new D(0);
  let maxKey: K | null = null;
  let maxDays = -1;
  for (const [k, d] of days) {
    if (d <= 0) continue;
    const share = new D(amount).times(d).div(total).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
    out.set(k, share.toNumber());
    sum = sum.plus(share);
    if (d > maxDays) {
      maxDays = d;
      maxKey = k;
    }
  }
  const rest = new D(amount).minus(sum).toNumber();
  if (rest && maxKey !== null) out.set(maxKey, out.get(maxKey)! + rest);
  return out;
}

/** đ / đơn vị (km, đơn): làm tròn VND ROUND_HALF_UP; không có đơn vị → null. */
export function costPerUnit(total: number, units: number | null | undefined): number | null {
  if (!units || units <= 0) return null;
  return new D(total).div(units).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();
}

export interface AsiaDiff {
  diff: number;
  limit: number;
  flag: boolean;
}
/** Chênh ERP − Asia; vượt max(500.000 đ; 5% số Asia) → cờ đỏ. Asia chưa có số → null. */
export function asiaDiff(erp: number, asia: number | null, p: Pick<FleetEfficiencyPolicy, 'asiaDiffMinVnd' | 'asiaDiffRatioPct'>): AsiaDiff | null {
  if (asia === null) return null;
  const diff = erp - asia;
  const limit = Math.max(p.asiaDiffMinVnd, new D(Math.abs(asia)).times(p.asiaDiffRatioPct).div(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber());
  return { diff, limit, flag: Math.abs(diff) > limit };
}

/** Chi phí sửa/km của xe > hệ số × bình quân đội xe (chỉ xét khi đội xe có chi phí sửa). */
export function repairHigh(perKm: number | null, fleetAvgPerKm: number | null, ratio: number): boolean {
  if (perKm === null || !fleetAvgPerKm || fleetAvgPerKm <= 0) return false;
  return perKm > fleetAvgPerKm * ratio;
}

/** Lốp thay sớm: thay lốp cùng vị trí khi km chạy từ lần thay trước < ngưỡng. */
export function tireEarly(prevKm: number | null, km: number | null, minKm: number): { kmSince: number | null; early: boolean } {
  if (prevKm === null || km === null) return { kmSince: null, early: false };
  const kmSince = r1(km - prevKm);
  return { kmSince, early: kmSince < minKm };
}

// ---------------------------------------------------------------------------
// Phiếu chi phí xe (9.3)
// ---------------------------------------------------------------------------
export const VEHICLE_EXPENSE_CATEGORIES = ['tire_patch', 'tire_replace', 'tire_blowout', 'repair', 'maintenance', 'toll', 'wash', 'other'] as const;
export type VehicleExpenseCategory = (typeof VEHICLE_EXPENSE_CATEGORIES)[number];
export const VEHICLE_EXPENSE_CATEGORY_LABELS: Record<VehicleExpenseCategory, string> = {
  tire_patch: 'Vá lốp',
  tire_replace: 'Thay lốp',
  tire_blowout: 'Nổ lốp',
  repair: 'Sửa chữa',
  maintenance: 'Bảo dưỡng',
  toll: 'Cầu đường',
  wash: 'Rửa xe',
  other: 'Khác',
};
/** Nhóm cột trên báo cáo hiệu quả */
export const EXPENSE_GROUPS = ['tire', 'repair', 'maintenance', 'toll', 'other'] as const;
export type ExpenseGroup = (typeof EXPENSE_GROUPS)[number];
export const EXPENSE_GROUP_LABELS: Record<ExpenseGroup, string> = {
  tire: 'Lốp (vá, thay, nổ)',
  repair: 'Sửa chữa',
  maintenance: 'Bảo dưỡng',
  toll: 'Cầu đường',
  other: 'Khác',
};
export const expenseGroupOf = (c: VehicleExpenseCategory): ExpenseGroup =>
  c === 'tire_patch' || c === 'tire_replace' || c === 'tire_blowout' ? 'tire' : c === 'repair' || c === 'maintenance' || c === 'toll' ? c : 'other';

export const VEHICLE_EXPENSE_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type VehicleExpenseStatus = (typeof VEHICLE_EXPENSE_STATUSES)[number];
export const VEHICLE_EXPENSE_STATUS_LABELS: Record<VehicleExpenseStatus, string> = { pending: 'Chờ duyệt', approved: 'Đã duyệt', rejected: 'Từ chối' };

/** Vị trí lốp (bắt buộc với thay lốp / nổ lốp để so lần thay trước cùng vị trí) */
export const TIRE_POSITIONS = ['FL', 'FR', 'ML_OUT', 'ML_IN', 'MR_OUT', 'MR_IN', 'RL_OUT', 'RL_IN', 'RR_OUT', 'RR_IN', 'SPARE'] as const;
export type TirePosition = (typeof TIRE_POSITIONS)[number];
export const TIRE_POSITION_LABELS: Record<TirePosition, string> = {
  FL: 'Trước trái',
  FR: 'Trước phải',
  ML_OUT: 'Giữa trái ngoài',
  ML_IN: 'Giữa trái trong',
  MR_OUT: 'Giữa phải ngoài',
  MR_IN: 'Giữa phải trong',
  RL_OUT: 'Sau trái ngoài',
  RL_IN: 'Sau trái trong',
  RR_OUT: 'Sau phải ngoài',
  RR_IN: 'Sau phải trong',
  SPARE: 'Lốp dự phòng',
};
export const expenseNeedsTirePosition = (c: VehicleExpenseCategory) => c === 'tire_replace' || c === 'tire_blowout';

// ---------------------------------------------------------------------------
// 9.4 File đối chiếu Asia (mẫu ERP tự định nghĩa – chưa có cột thật của Asia, DEC-215)
// ---------------------------------------------------------------------------
export const ASIA_COST_TEMPLATE_HEADERS = ['Kỳ (YYYY-MM)', 'Mã đối tượng', 'Loại (xe|tài)', 'Khoản mục', 'Số tiền', 'Ghi chú'] as const;
export type AsiaCostObjectKind = 'vehicle' | 'driver';
export const ASIA_COST_KIND_LABELS: Record<AsiaCostObjectKind, string> = { vehicle: 'xe', driver: 'tài' };

/** "xe" | "tài" | "tài xế" | "vehicle" | "driver" (bỏ dấu, không phân biệt hoa thường) → loại; khác → null. */
export function parseAsiaCostKind(s: unknown): AsiaCostObjectKind | null {
  const v = String(s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase()
    .trim();
  if (v === 'xe' || v === 'vehicle') return 'vehicle';
  if (v === 'tai' || v === 'tai xe' || v === 'driver') return 'driver';
  return null;
}
